import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import type { FeGeschoss, FeKreis, FeMatrixzeile, FeVorgabe, FunktionserhaltStand } from './elektro/funktionserhalt';
import { ABSCHAETZUNG_HINWEIS, PDF_HINWEIS } from './elektro/funktionserhalt';
import { zerlegeBefund } from './elektro/blitzschutz';
import { PageHeader } from '../rahmen/PageHeader';
import './elektro/blitzschutz.css';
import {
  ContentSection, DataTable, ErrorState, KpiCard, KpiReihe, LoadingState, StatusBadge,
  type Spalte, type Zustand,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * Funktionserhalt-Systemnachweis (W2-C3, 16.10.2026) — Abschnitt „Trassen und Kabel".
 *
 * Die Seite LIEST `GET …/elektro/funktionserhalt`: Anforderungsmatrix nach
 * MLAR 5.3.2, Soll/Ist je SV-Kreis aus der Kabelbemessung, Tragsysteme,
 * E90-Längenvergleich und die Abschätzung von Füllgrad und Last je Geschoss.
 * Ist die Kabelbemessung nicht gelaufen, zeigt sie das und Matrix und Trassen.
 */
export function ElektroFunktionserhalt() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <LoadingState />;
  return <FunktionserhaltAnsicht api={api} projektId={projekt.id} />;
}

function zustandVon(status: string): Zustand {
  return status === 'warnung' ? 'warnung' : status === 'ok' ? 'gut' : 'info';
}

function Befundliste({ befunde }: { befunde: string[] }) {
  if (befunde.length === 0) return null;
  return (
    <ul className="liste">
      {befunde.map((b, i) => {
        const { stufe, text } = zerlegeBefund(b);
        const z: Zustand = stufe === 'fehler' ? 'fehler' : stufe === 'warnung' ? 'warnung' : 'info';
        return <li key={`${i}-${text.slice(0, 24)}`}><StatusBadge zustand={z} text={text} /></li>;
      })}
    </ul>
  );
}

export function FunktionserhaltAnsicht({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [stand, setStand] = useState<FunktionserhaltStand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    api.elektroFunktionserhalt(projektId)
      .then((s) => { if (aktiv) { setStand(s); setFehler(null); } })
      .catch((e: unknown) => { if (aktiv) setFehler(e instanceof Error ? e.message : String(e)); });
    return () => { aktiv = false; };
  }, [api, projektId]);

  async function laden(art: 'pdf' | 'excel') {
    setLaeuft(art);
    try {
      if (art === 'pdf') await api.elektroFunktionserhaltPdfLaden(projektId);
      else await api.elektroFunktionserhaltListen(projektId);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  }

  const aktionen = (
    <>
      <button type="button" className="m-knopf" disabled={laeuft !== null}
              onClick={() => { void laden('excel'); }}>
        Excel-Mappe
      </button>
      <button type="button" className="m-knopf" disabled={laeuft !== null} title={PDF_HINWEIS}
              onClick={() => { void laden('pdf'); }}>
        Nachweis als PDF
      </button>
    </>
  );

  return (
    <div className="seite">
      <PageHeader
        titel="Funktionserhalt-Systemnachweis"
        beschreibung="Anforderungen je Sicherheitsanlage nach MLAR 5.3.2, Soll gegen Ist je SV-Kreis aus der Kabelbemessung, Tragsysteme der E30-/E90-Trassen und eine Abschätzung von Füllgrad und Last je Geschoss. Gerechnet beim Abruf, nur lesend."
        aktionen={aktionen}
      />
      <p className="hinweis">{PDF_HINWEIS}</p>
      {fehler && <ErrorState titel="Der Funktionserhalt-Nachweis konnte nicht geladen werden." technisch={fehler} />}
      {!fehler && !stand && <LoadingState />}
      {stand && <Inhalt stand={stand} />}
    </div>
  );
}

function Inhalt({ stand }: { stand: FunktionserhaltStand }) {
  const k = stand.kennzahlen;
  return (
    <>
      {!k.kabelbemessungGelaufen && (
        <p className="hinweis hinweis-warnung" data-testid="leerfall">
          Kabelbemessung nicht gelaufen — Soll/Ist je SV-Kreis, E90-Bedarf und die Abschätzung der Belegung fehlen.
          Gezeigt werden Anforderungsmatrix und Trassen.
        </p>
      )}
      <p className="hinweis">{ABSCHAETZUNG_HINWEIS}</p>
      <KpiReihe>
        <KpiCard titel="SV-Kreise" wert={k.svKreise} vergleich={`${k.zugeordnet} zugeordnet · ${k.offen} offen`} />
        <KpiCard titel="SV-Zuleitungen" wert={k.svZuleitungen} />
        <KpiCard titel="E30-Trasse" wert={k.e30TrasseM} einheit="m" nachkomma={0} />
        <KpiCard titel="E90-Trasse" wert={k.e90TrasseM} einheit="m" nachkomma={0}
                 vergleich={`Bedarf ${Math.round(k.e90BedarfM)} m Kabel`} />
        <KpiCard titel="Warnungen" wert={k.warnungen} zustand={k.warnungen > 0 ? 'warnung' : 'gut'}
                 zustandText={k.warnungen > 0 ? 'prüfen' : 'keine'} />
      </KpiReihe>
      <Matrix zeilen={stand.matrix} />
      {k.kabelbemessungGelaufen && <Kreise zeilen={stand.kreise} />}
      <ContentSection titel="Tragsysteme und E90-Längenvergleich"
                      beschreibung="Längenvergleich, kein Trassenweg: Ein Kabel liegt nicht vollständig auf Trassen.">
        <ul className="liste klein">
          {stand.tragsysteme.map((t) => (
            <li key={`${t.klasse}-${t.typ}`}><strong>{t.klasse}</strong> {t.typ}: {t.teile} Teile, {Math.round(t.laengeM)} m</li>
          ))}
        </ul>
        <p className="klein">
          E90: {stand.e90.kreise} Kreise, {Math.round(stand.e90.kabelLaengeM)} m Kabel gegen {Math.round(stand.e90.trassenLaengeM)} m
          Trasse in {stand.e90.trassenGeschosse} Geschoss(en)
        </p>
        <Befundliste befunde={stand.e90.befunde} />
      </ContentSection>
      <Geschosse zeilen={stand.geschosse} />
      <Vorgaben zeilen={stand.vorgaben} />
      {stand.befunde.length > 0 && (
        <ContentSection titel="Befunde">
          <Befundliste befunde={stand.befunde} />
        </ContentSection>
      )}
    </>
  );
}

function Matrix({ zeilen }: { zeilen: FeMatrixzeile[] }) {
  const spalten: Spalte<FeMatrixzeile>[] = [
    { schluessel: 'titel', titel: 'Anlage', wert: (z) => z.titel },
    { schluessel: 'soll', titel: 'Soll', wert: (z) => z.soll },
    { schluessel: 'muster', titel: 'Erkennung (Wortanfang)', wert: (z) => z.muster },
    { schluessel: 'kreise', titel: 'Kreise', art: 'zahl', wert: (z) => z.kreise },
    { schluessel: 'warn', titel: 'Warnungen', art: 'zahl', wert: (z) => z.warnungen },
    {
      schluessel: 'b', titel: 'bestätigt', wert: (z) => (z.bestaetigt ? 1 : 0),
      inhalt: (z) => <StatusBadge zustand={z.bestaetigt ? 'gut' : 'warnung'} text={z.bestaetigt ? 'ja' : 'nein'} klein />,
    },
  ];
  return (
    <ContentSection titel="Anforderungsmatrix nach MLAR 5.3.2"
                    beschreibung="Dauer des Funktionserhalts je Sicherheitsanlage (Normtext ungeprüft). Die Zuordnung der Kreise über Wortanfänge ist eine Annahme (ELT-61); ob ein Aufzug Feuerwehraufzug ist, legt die Fachplanung fest (ELT-66).">
      <DataTable name={`Anlagen (${zeilen.length})`} spalten={spalten} zeilen={zeilen} schluessel={(z) => z.anlage} dicht />
    </ContentSection>
  );
}

function Kreise({ zeilen }: { zeilen: FeKreis[] }) {
  const [nurAuffaellig, setNurAuffaellig] = useState(true);
  const sicht = nurAuffaellig ? zeilen.filter((z) => z.status !== 'ok') : zeilen;
  const spalten: Spalte<FeKreis>[] = [
    { schluessel: 'art', titel: 'Art', wert: (z) => z.art },
    { schluessel: 'k', titel: 'Kennzeichen', wert: (z) => z.kennzeichen },
    { schluessel: 'v', titel: 'Verteiler', wert: (z) => z.verteiler },
    { schluessel: 'g', titel: 'Geschoss', wert: (z) => z.geschoss ?? '' },
    { schluessel: 'b', titel: 'Bezeichnung', wert: (z) => z.bezeichnung ?? '' },
    { schluessel: 'a', titel: 'Anlage', wert: (z) => z.anlageTitel ?? '—' },
    { schluessel: 'soll', titel: 'Soll', wert: (z) => z.soll },
    { schluessel: 'typ', titel: 'Kabeltyp', wert: (z) => z.kabeltyp ?? '' },
    { schluessel: 'ist', titel: 'Ist', wert: (z) => z.ist },
    { schluessel: 'l', titel: 'Länge', einheit: 'm', art: 'zahl', nachkomma: 0, wert: (z) => z.laengeM ?? '' },
    {
      schluessel: 's', titel: 'Status', wert: (z) => z.status,
      inhalt: (z) => <StatusBadge zustand={zustandVon(z.status)} text={z.status} klein />,
    },
  ];
  return (
    <ContentSection titel="SV-Kreise und SV-Zuleitungen" beschreibung="Soll nach der Anlage, Ist aus dem bemessenen Kabeltyp.">
      <label className="klein">
        <input type="checkbox" checked={nurAuffaellig} onChange={(e) => setNurAuffaellig(e.target.checked)} /> nur Warnungen und offene
      </label>
      <DataTable name={`Kreise (${sicht.length} von ${zeilen.length})`} spalten={spalten} zeilen={sicht}
                 schluessel={(z) => `${z.art}-${z.kennzeichen}`} dicht />
    </ContentSection>
  );
}

function Geschosse({ zeilen }: { zeilen: FeGeschoss[] }) {
  const spalten: Spalte<FeGeschoss>[] = [
    { schluessel: 'g', titel: 'Geschoss', wert: (z) => z.geschoss },
    { schluessel: 'k', titel: 'Klasse', wert: (z) => z.klasse },
    { schluessel: 'tl', titel: 'Trasse', einheit: 'm', art: 'zahl', nachkomma: 0, wert: (z) => z.trassenLaengeM },
    { schluessel: 'kb', titel: 'Kabel', art: 'zahl', wert: (z) => z.kabel },
    { schluessel: 'kl', titel: 'Kabellänge', einheit: 'm', art: 'zahl', nachkomma: 0, wert: (z) => z.kabelLaengeM },
    { schluessel: 'f', titel: 'Füllgrad', einheit: '%', art: 'zahl', nachkomma: 1, wert: (z) => z.fuellgradProzent ?? '' },
    { schluessel: 'l', titel: 'Last', einheit: 'kg/m', art: 'zahl', nachkomma: 2, wert: (z) => z.lastKgJeM ?? '' },
    { schluessel: 'z', titel: 'zulässig', einheit: 'kg/m', wert: (z) => (z.lastZulKgJeM === null ? 'offen' : String(z.lastZulKgJeM)) },
    {
      schluessel: 's', titel: 'Status', wert: (z) => z.status,
      inhalt: (z) => <StatusBadge zustand={zustandVon(z.status)} text={z.status} klein />,
    },
  ];
  return (
    <ContentSection titel="Füllgrad und Last — Abschätzung je Geschoss" beschreibung={ABSCHAETZUNG_HINWEIS}>
      <DataTable name={`Geschosse (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => `${z.geschoss}-${z.klasse}`} dicht />
      <Befundliste befunde={zeilen.flatMap((z) => z.befunde.map((b) => `${b} (${z.geschoss} ${z.klasse})`))} />
    </ContentSection>
  );
}

function Vorgaben({ zeilen }: { zeilen: FeVorgabe[] }) {
  const spalten: Spalte<FeVorgabe>[] = [
    { schluessel: 'f', titel: 'Vorgabe', wert: (z) => z.feld },
    { schluessel: 'v', titel: 'Verwendung', wert: (z) => z.verwendung ?? '' },
    { schluessel: 'w', titel: 'Wert', wert: (z) => z.wert },
    { schluessel: 'e', titel: 'Einheit', wert: (z) => z.einheit },
    { schluessel: 'h', titel: 'Herkunft', wert: (z) => z.herkunft + (z.rueckfall ? ' (Rückfall)' : '') },
    {
      schluessel: 'b', titel: 'bestätigt', wert: (z) => (z.bestaetigt ? 1 : 0),
      inhalt: (z) => <StatusBadge zustand={z.bestaetigt ? 'gut' : 'warnung'} text={z.bestaetigt ? 'ja' : 'nein'} klein />,
    },
  ];
  return (
    <ContentSection titel="Vorgaben" beschreibung="Alle Vorgaben entstehen unbestätigt (Migration 1010).">
      <DataTable name={`Vorgaben (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => `${z.feld}-${z.verwendung ?? ''}`} dicht />
    </ContentSection>
  );
}
